import {
  ICopyDefaultToContractBody,
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreviewResponse,
} from "types/DefaultRules/DefaultRules";
import {
  IProcedureDiscountRuleset,
  IProcedureDiscountRulesetCreateRequest,
  IProcedureDiscountRulesetUpdateRequest,
} from "types/ProcedureDiscountRuleset/ProcedureDiscountRuleset";
import {
  IAllProcedureDiscountRulesetResponse,
  IProcedureDiscountRulesetResponse,
} from "types/ProcedureDiscountRuleset/ProcedureDiscountRulesetResponse";

import { api } from ".";

const procedureDiscountRulesetQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllDiscountRulesets: builder.query<
      IAllProcedureDiscountRulesetResponse,
      { isDefault?: boolean } | null | void
    >({
      query: (params) => {
        const search =
          params && params.isDefault !== undefined
            ? `?isDefault=${params.isDefault}`
            : "";
        return `payer-rules/all${search}`;
      },
      providesTags: ["MpprRulesets"],
    }),
    getDiscountRulesetById: builder.query<
      IProcedureDiscountRulesetResponse,
      string | undefined
    >({
      query: (id: string) => `payer-rules/${id}`,
      providesTags: (_r, _e, id) => [
        { type: "MpprRulesets", id: id ?? "LIST" },
      ],
    }),
    createDiscountRuleset: builder.mutation<
      IProcedureDiscountRuleset,
      IProcedureDiscountRulesetCreateRequest
    >({
      query: (newRuleset) => ({
        url: "payer-rules",
        method: "POST",
        body: newRuleset,
      }),
      invalidatesTags: ["MpprRulesets"],
    }),
    updateDiscountRuleset: builder.mutation<
      IProcedureDiscountRuleset,
      IProcedureDiscountRulesetUpdateRequest
    >({
      query: (updatedRuleset) => ({
        url: `payer-rules/${updatedRuleset.id}`,
        method: "PUT",
        body: updatedRuleset,
      }),
      invalidatesTags: ["MpprRulesets", "DefaultRulePropagation"],
    }),
    deleteDiscountRuleset: builder.mutation<any, string>({
      query: (id) => ({
        url: `payer-rules/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["MpprRulesets", "DefaultRulePropagation"],
    }),
    cloneDiscountRulesetAsDefault: builder.mutation<
      IProcedureDiscountRulesetResponse,
      string
    >({
      query: (id) => ({
        url: `payer-rules/${id}/clone-as-default`,
        method: "POST",
      }),
      invalidatesTags: ["MpprRulesets"],
    }),
    copyDiscountRulesetToContract: builder.mutation<
      IProcedureDiscountRulesetResponse,
      { id: string; body: ICopyDefaultToContractBody }
    >({
      query: ({ id, body }) => ({
        url: `payer-rules/${id}/copy-to-contract`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["MpprRulesets", "ContractFeeSchedules"],
    }),
    getDiscountRulesetPropagationPreview: builder.query<
      IDefaultRulePropagationPreviewResponse,
      string
    >({
      query: (id) => `payer-rules/${id}/propagate/preview`,
      providesTags: (_r, _e, id) => [
        { type: "DefaultRulePropagation", id: `mppr:${id}` },
      ],
    }),
    applyDiscountRulesetPropagation: builder.mutation<
      IDefaultRulePropagationApplyResponse,
      { id: string; requests: IDefaultRulePropagationApplyRequest[] }
    >({
      query: ({ id, requests }) => ({
        url: `payer-rules/${id}/propagate`,
        method: "POST",
        body: { requests },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "MpprRulesets",
        { type: "DefaultRulePropagation", id: `mppr:${id}` },
      ],
    }),
  }),
});

export const {
  useGetAllDiscountRulesetsQuery,
  useGetDiscountRulesetByIdQuery,
  useCreateDiscountRulesetMutation,
  useUpdateDiscountRulesetMutation,
  useDeleteDiscountRulesetMutation,
  useCloneDiscountRulesetAsDefaultMutation,
  useCopyDiscountRulesetToContractMutation,
  useGetDiscountRulesetPropagationPreviewQuery,
  useLazyGetDiscountRulesetPropagationPreviewQuery,
  useApplyDiscountRulesetPropagationMutation,
} = procedureDiscountRulesetQueries;
